/**
 * Armadillo's automatic assignment: kept by the report's registry orders,
 * so read-only. Its fee follows the orders (a quote recorded, an order
 * closed), and it is billed in Backoffice as a silent request nobody is
 * emailed about. An admin can adjust that fee by a signed amount where the
 * order rules cannot match the invoice, and record a charge on it once
 * every order closed or failed (a failed trace Armadillo still charges). A
 * manual assignment locked to Armadillo takes it over: the orders move
 * there, the Hub keeps that assignment's fee from them in the same way,
 * and this one closes, its Backoffice request kept at £0.
 */

import { useState } from 'react';

import { Badge, Button, Card, Group, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import * as Sentry from '@sentry/react';

import { useActorNames } from '@/hooks/useActorNames';
import { vnextApiClient } from '@/services/vnext-api';
import {
  assignmentLabelFor,
  describeFeeAdjustment,
  describeManagedFee,
  describeTakenOverBackoffice,
  isAssignmentCancelled,
  REGISTRY_BACKOFFICE_STATE_LABELS,
  type BackofficeAssignment,
} from '@/types/np-assignments';
import { describeLastChange } from '@/utils/commercialChanges';
import { useNpAssignmentsContext } from './NpAssignmentsContext';
import { RegistryFeeAdjustmentControl } from './RegistryFeeAdjustmentModal';
import { RegistryOrderLines } from './RegistryOrdersBilled';

interface ManagedAssignmentCardProps {
  assignment: BackofficeAssignment;
  index: number;
}

export function ManagedAssignmentCard({ assignment, index }: Readonly<ManagedAssignmentCardProps>) {
  const { assignments } = useNpAssignmentsContext();
  const takenOver = assignment.takenOverById !== null;
  const closed = isAssignmentCancelled(assignment);

  return (
    <Card
      withBorder
      padding="md"
      radius="md"
      data-testid={`assignment-card-${assignment.id}`}
      style={{
        borderLeft: '4px solid var(--mantine-color-gray-5)',
        opacity: closed ? 0.75 : undefined,
      }}
    >
      <Stack gap="sm">
        <Group gap="xs" wrap="wrap">
          <Badge color="gray" variant="filled">
            A{index + 1}
          </Badge>
          <Text fw={600}>{assignment.networkPartner?.name ?? 'Armadillo'}</Text>
          <Badge color="grape" variant="light" data-testid={`managed-badge-${assignment.id}`}>
            {takenOver ? 'Automatic · taken over' : 'Automatic · registry orders'}
          </Badge>
          {closed && (
            <Badge color="gray" variant="light">
              Cancelled
            </Badge>
          )}
        </Group>
        {takenOver ? (
          <TakenOverBody
            assignment={assignment}
            takenOverBy={assignmentLabelFor(assignments, assignment.takenOverById)}
          />
        ) : (
          <LiveBody assignment={assignment} />
        )}
        {assignment.notes && (
          <Text size="xs" c="dimmed">
            {assignment.notes}
          </Text>
        )}
      </Stack>
    </Card>
  );
}

/** The automatic assignment while it bills: its orders, its fee and where
 * Backoffice stands. */
function LiveBody({ assignment }: Readonly<{ assignment: BackofficeAssignment }>) {
  return (
    <>
      {assignment.registryOrders.length > 0 ? (
        <RegistryOrderLines orders={assignment.registryOrders} />
      ) : (
        <Text size="xs" c="dimmed">
          No registry orders are billed here.
        </Text>
      )}
      <Group gap="md" wrap="wrap" justify="space-between">
        <Group gap="md" wrap="wrap">
          <Text size="sm" fw={600} data-testid={`managed-fee-${assignment.id}`}>
            {describeManagedFee(assignment)}
          </Text>
          {assignment.backofficeFee !== null && (
            <Text size="sm" c="dimmed">
              Backoffice: £{assignment.backofficeFee}
            </Text>
          )}
        </Group>
        <RegistryFeeAdjustmentControl assignment={assignment} />
      </Group>
      <FeeAdjustmentLines assignment={assignment} />
      <BackofficeStateLine
        assignment={assignment}
        label={
          assignment.backofficeState
            ? REGISTRY_BACKOFFICE_STATE_LABELS[assignment.backofficeState]
            : null
        }
      />
    </>
  );
}

/** How an adjusted fee comes about, and who adjusted it. Nothing without
 * an adjustment. */
function FeeAdjustmentLines({ assignment }: Readonly<{ assignment: BackofficeAssignment }>) {
  const nameFor = useActorNames();
  const breakdown = describeFeeAdjustment(assignment);
  if (!breakdown) {
    return null;
  }
  const setBy =
    assignment.feeAdjustmentSetByUserId !== null || assignment.feeAdjustmentSetAt !== null;
  return (
    <Stack gap={2}>
      <Text size="xs" data-testid={`fee-breakdown-${assignment.id}`}>
        {breakdown}
      </Text>
      {setBy && (
        <Text size="xs" c="dimmed" data-testid={`fee-adjusted-by-${assignment.id}`}>
          Adjustment ·{' '}
          {describeLastChange(
            {
              actorUserId: assignment.feeAdjustmentSetByUserId,
              createdAt: assignment.feeAdjustmentSetAt,
            },
            nameFor
          )}
        </Text>
      )}
    </Stack>
  );
}

/** Taken over by a manual assignment: the orders moved there, so only what
 * Backoffice still holds is left to say. */
function TakenOverBody({
  assignment,
  takenOverBy,
}: Readonly<{ assignment: BackofficeAssignment; takenOverBy: string | null }>) {
  return (
    <>
      <Text size="sm" data-testid={`taken-over-${assignment.id}`}>
        Taken over by {takenOverBy ?? 'a manual assignment'}: its registry orders are billed there
      </Text>
      <BackofficeStateLine
        assignment={assignment}
        label={describeTakenOverBackoffice(assignment)}
      />
    </>
  );
}

/** Where Backoffice stands, and the way to catch it up when it is behind.
 * A push of the last couple of minutes (`syncing`) is left to land. */
function BackofficeStateLine({
  assignment,
  label,
}: Readonly<{ assignment: BackofficeAssignment; label: string | null }>) {
  if (!label) {
    return null;
  }
  const behind = assignment.backofficeState === 'behind';
  return (
    <Stack gap={4} data-testid={`backoffice-state-${assignment.id}`}>
      <Group gap="xs" wrap="wrap">
        <Text size="xs" c={behind ? 'orange.8' : 'dimmed'}>
          {label}
        </Text>
        {behind && <RetrySync reportId={assignment.reportId} />}
      </Group>
      {behind && assignment.backofficeSyncMarker.error && (
        <Text size="xs" c="dimmed">
          {assignment.backofficeSyncMarker.error}
        </Text>
      )}
    </Stack>
  );
}

/** Work the assignment out again now and push it to Backoffice, rather
 * than wait for the delivery worker's next pass; then re-read the cards. */
function RetrySync({ reportId }: Readonly<{ reportId: string }>) {
  const { actions } = useNpAssignmentsContext();
  const [busy, setBusy] = useState(false);

  const retry = async () => {
    setBusy(true);
    try {
      await vnextApiClient.syncRegistryPartnerAssignment(reportId);
      await actions.refetch();
    } catch (err) {
      Sentry.captureException(err);
      notifications.show({
        title: 'Could not bring Backoffice in step',
        message: err instanceof Error ? err.message : String(err),
        color: 'red',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      size="compact-xs"
      variant="light"
      color="orange"
      loading={busy}
      onClick={() => void retry()}
      data-testid={`registry-sync-retry-${reportId}`}
    >
      Retry
    </Button>
  );
}
